import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Download, Copy, Check, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import DeliverableFeedback from "@/components/DeliverableFeedback";

const RESUME_TO_LETTER = {
  executive: "executive", leadership: "executive",
  modern: "modern", creative: "modern",
  minimal: "minimal", professional: "professional",
};

/**
 * CoverLetterModal - side-drawer for AI-drafted cover letters.
 * Opens with a "professional" tone by default; user can swap tone
 * or add a recipient name and the letter regenerates.
 *
 * Contract preserved from the original inline component:
 *   props: { analysisId, alignedId, onClose }
 */
export default function CoverLetterModal({ analysisId, alignedId, onClose }) {
  const [tone, setTone] = useState("professional");
  const [recipient, setRecipient] = useState("");
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [letterTemplates, setLetterTemplates] = useState([]);
  const [letterDesign, setLetterDesign] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data }, alignedRes] = await Promise.all([
          api.get("/resume/templates"),
          alignedId ? api.get(`/resume/aligned/${alignedId}`).catch(() => null) : Promise.resolve(null),
        ]);
        if (!alive) return;
        setLetterTemplates(data.letter_templates || []);
        // Recommend the letter that matches the resume design in use.
        const resumeTpl = alignedRes?.data?.template_id || data.default_template;
        let match = RESUME_TO_LETTER[resumeTpl] || "professional";
        const info = (data.letter_templates || []).find((t) => t.id === match);
        if (info?.locked) match = "professional";
        setLetterDesign(match);
      } catch (e) {
        console.warn("[CoverLetter] letter templates fetch failed", e);
      }
    })();
    return () => { alive = false; };
  }, [alignedId]);

  const generate = React.useCallback(async (nextTone, nextRecipient) => {
    setGenerating(true);
    setErr("");
    try {
      const { data } = await api.post("/resume/cover-letter", {
        analysis_id: analysisId,
        aligned_id: alignedId,
        tone: nextTone,
        recipient_name: (nextRecipient ?? recipient).trim() || undefined,
      });
      setContent(data.content || "");
      setMeta({
        role: data.job_title,
        company: data.company,
        created_at: data.created_at,
        id: data.cover_letter_id,
      });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not generate the cover letter.");
    } finally {
      setGenerating(false);
    }
  }, [analysisId, alignedId, recipient]);

  useEffect(() => {
    generate("professional");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard permission denied or unavailable - non-fatal.
      console.debug("[ResumeAlignment] clipboard copy failed", e);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const download = async (fmt = "docx") => {
    if (!content) return;
    setDownloading(true);
    try {
      const safe = `${meta?.role || "cover-letter"}-${meta?.company || ""}`
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase() || "cover-letter";
      const res = await api.post(
        "/resume/export-docx",
        {
          content,
          filename: safe,
          kind: "cover-letter",
          format: fmt,
          template_id: letterDesign || undefined,
          aligned_id: alignedId || undefined,
        },
        { responseType: "blob" },
      );
      track("templates.letter_download", { format: fmt, template_id: letterDesign || "default" });
      const mime = fmt === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn(`[CoverLetter] .${fmt} download failed`, e);
      setErr(`Could not export as .${fmt}. Please try again or use Copy.`);
    } finally {
      setDownloading(false);
    }
  };

  const wordCount = (content || "").trim().split(/\s+/).filter(Boolean).length;

  return createPortal(
    <div
      data-testid="cover-letter-modal"
      className="fixed inset-0 z-[70]"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="fixed inset-y-0 left-0 z-[71] w-full max-w-2xl rounded-r-2xl border-r border-y border-zinc-800 bg-zinc-950 shadow-2xl flex flex-col animate-slide-in-left"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-zinc-800/70">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-0.5">cover letter</div>
            <div className="text-lg font-semibold text-zinc-50">
              {meta?.role ? `${meta.role}${meta.company ? ` @ ${meta.company}` : ""}` : "Draft cover letter"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="cover-letter-close"
            className="text-zinc-500 hover:text-zinc-100 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 sm:px-6 pt-4 pb-3 flex flex-wrap items-center gap-2 border-b border-zinc-800/50">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Tone</span>
          {[
            { id: "professional", label: "Professional" },
            { id: "executive", label: "Executive" },
            { id: "warm", label: "Warm" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { setTone(t.id); generate(t.id, recipient); }}
              disabled={generating}
              data-testid={`cover-letter-tone-${t.id}`}
              className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                tone === t.id
                  ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                  : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
              } ${generating ? "opacity-60 cursor-wait" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {letterTemplates.length > 0 && (
          <div className="px-4 sm:px-6 pt-2 pb-3 flex flex-wrap items-center gap-2 border-b border-zinc-800/50">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Design</span>
            {letterTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => { if (!t.locked) setLetterDesign(t.id); }}
                disabled={t.locked}
                data-testid={`letter-design-${t.id}`}
                title={t.locked ? "Upgrade to unlock this letter design" : t.tagline}
                className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 border transition-colors ${
                  letterDesign === t.id
                    ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                    : t.locked
                      ? "border-zinc-800 text-zinc-600 cursor-not-allowed"
                      : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
                }`}
              >
                {t.locked && <Lock className="h-2.5 w-2.5" />}
                {t.name.replace(" Letter", "")}
              </button>
            ))}
            <span className="text-[10px] text-zinc-600">Matches your resume design in the PDF.</span>
          </div>
        )}

        <div className="px-4 sm:px-6 pt-2 pb-3 flex flex-wrap items-center gap-2 border-b border-zinc-800/50">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Dear</span>
          <input
            type="text"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            onBlur={() => { if (!generating) generate(tone, recipient); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!generating) generate(tone, recipient); } }}
            placeholder="Hiring manager name (optional)"
            data-testid="cover-letter-recipient"
            className="flex-1 min-w-[220px] bg-zinc-900 border border-zinc-800 rounded-md px-3 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/40"
          />
          <span className="text-[10px] text-zinc-600">Leave blank for a company-team salutation.</span>
        </div>

        <div className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto">
          {generating && !content ? (
            <div
              data-testid="cover-letter-loading"
              className="h-64 flex flex-col items-center justify-center gap-2 text-zinc-500"
            >
              <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
              <div className="text-xs uppercase tracking-widest">Drafting your cover letter…</div>
            </div>
          ) : err ? (
            <div className="text-sm text-rose-300">{err}</div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="cover-letter-textarea"
              className="w-full h-full min-h-[320px] bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-sm text-zinc-100 leading-relaxed font-serif focus:outline-none focus:border-amber-500/40 resize-none"
              placeholder="Your cover letter will appear here…"
            />
          )}
        </div>

        {content && !generating && (
          <div className="px-4 sm:px-6 pb-2">
            <DeliverableFeedback
              deliverable="cover_letter"
              label="Cover Letter"
              entityKey={alignedId || analysisId || "cover"}
              context={{ aligned_id: alignedId, analysis_id: analysisId }}
            />
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 border-t border-zinc-800/70 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-zinc-500 tabular-nums">
            {wordCount > 0 ? `${wordCount} words` : "\u00A0"}
            {generating && content ? <span className="ml-2 text-amber-400">Regenerating…</span> : ""}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copy}
              disabled={!content || generating}
              data-testid="cover-letter-copy"
              className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-md px-3 py-1.5 disabled:opacity-60 transition-colors"
            >
              {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={() => download("pdf")}
              disabled={!content || generating || downloading}
              data-testid="cover-letter-download-pdf"
              className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-md px-3 py-1.5 disabled:opacity-60 transition-colors"
              title="Download as PDF (recommended for email)"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              PDF
            </button>
            <button
              onClick={() => download("docx")}
              disabled={!content || generating || downloading}
              data-testid="cover-letter-download"
              className="inline-flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 disabled:opacity-60 transition-colors"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              {downloading ? "Preparing…" : "Download .docx"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
